import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { invoicesAPI, projectsAPI } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth.store'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  ArrowLeft,
  Receipt,
  Loader2,
  Check,
  Link2,
  Unlink,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Building2,
  Calendar,
  Hash,
  DollarSign,
  Pencil,
  Plus,
  X,
} from 'lucide-react'

// ==================== CONSTANTS ====================

const STATUS_LABELS: Record<string, string> = {
  PROCESSING: 'Processando',
  REVIEW: 'Em Revisao',
  CONFIRMED: 'Confirmada',
  LINKED: 'Vinculada',
  CANCELLED: 'Cancelada',
}

const STATUS_COLORS: Record<string, string> = {
  PROCESSING: 'bg-blue-100 text-blue-700',
  REVIEW: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  LINKED: 'bg-purple-100 text-purple-700',
  CANCELLED: 'bg-neutral-100 text-neutral-600',
}

const TYPE_LABELS: Record<string, string> = {
  OBRA: 'Obra',
  OPERACIONAL: 'Operacional',
}

// ==================== HELPERS ====================

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('pt-BR')
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '-'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function toInputDate(value: string | null | undefined): string {
  if (!value) return ''
  return new Date(value).toISOString().split('T')[0]
}

// ==================== COMPONENT ====================

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canDelete = usePermission('invoices:delete')

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [editMode, setEditMode] = useState(false)

  // Form state for REVIEW status and CONFIRMED edit mode
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    issueDate: '',
    totalValue: '',
    type: 'OBRA' as 'OBRA' | 'OPERACIONAL',
    projectId: '',
    observations: '',
    supplierName: '',
    supplierCnpj: '',
    items: [] as Array<{
      id?: string
      description: string
      quantity: string
      unit: string
      unitPrice: string
      totalPrice: string
      _key: string
    }>,
  })

  // ==================== QUERIES ====================

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesAPI.getById(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const inv = query.state.data as any
      return inv?.status === 'PROCESSING' ? 3000 : false
    },
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsAPI.list(),
    enabled: !!invoice && (invoice.status === 'REVIEW' || invoice.status === 'CONFIRMED'),
  })

  const projects = (projectsData as any)?.data ?? (projectsData as any)?.projects ?? []

  const { data: suggestions, isLoading: loadingSuggestions } = useQuery({
    queryKey: ['invoice-suggestions', id],
    queryFn: () => invoicesAPI.getTransactionSuggestions(id!),
    enabled: !!invoice && invoice.status === 'CONFIRMED',
  })

  // Populate form when invoice loads in REVIEW status or when entering edit mode
  useEffect(() => {
    if (!invoice) return
    if (invoice.status === 'REVIEW' || editMode) {
      setFormData({
        invoiceNumber: invoice.invoiceNumber || '',
        issueDate: toInputDate(invoice.issueDate),
        totalValue: invoice.totalValue != null ? String(invoice.totalValue) : '',
        type: invoice.type || 'OBRA',
        projectId: invoice.projectId || '',
        observations: invoice.observations || '',
        supplierName: invoice.supplier?.name || invoice.ocrSupplierName || '',
        supplierCnpj: invoice.supplier?.document || invoice.ocrSupplierCnpj || '',
        items: (invoice.items || []).map((item: any) => ({
          id: item.id,
          description: item.description || '',
          quantity: item.quantity != null ? String(item.quantity) : '',
          unit: item.unit || '',
          unitPrice: item.unitPrice != null ? String(item.unitPrice) : '',
          totalPrice: item.totalPrice != null ? String(item.totalPrice) : '',
          _key: item.id || `_new_${Math.random().toString(36).slice(2)}`,
        })),
      })
    }
  }, [invoice, editMode])

  // ==================== IMAGE BLOB (must be before early returns) ====================

  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!invoice?.imagePath) return

    const apiBase = import.meta.env.VITE_API_URL || '/api/v1'
    const url = invoice.imagePath.startsWith('http')
      ? invoice.imagePath
      : `${apiBase}${invoice.imagePath.replace('/api/v1', '')}`

    const { accessToken } = useAuthStore.getState()

    fetch(url, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load image')
        return res.blob()
      })
      .then((blob) => {
        setImageBlobUrl(URL.createObjectURL(blob))
      })
      .catch(() => setImageBlobUrl(null))

    return () => {
      setImageBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
    }
  }, [invoice?.imagePath])

  // ==================== MUTATIONS ====================

  const confirmMutation = useMutation({
    mutationFn: () => {
      const cleanedItems = formData.items
        .filter((item) => item.description.trim() !== '')
        .map((item) => ({
          id: item.id,
          description: item.description.trim(),
          quantity: Number(item.quantity) || 0,
          unit: item.unit || undefined,
          unitPrice: Number(item.unitPrice) || 0,
          totalPrice: Number(item.totalPrice) || 0,
        }))

      return invoicesAPI.confirm(id!, {
        invoiceNumber: formData.invoiceNumber || undefined,
        issueDate: formData.issueDate || undefined,
        totalValue: formData.totalValue ? Number(formData.totalValue) : undefined,
        type: formData.type,
        projectId: formData.type === 'OBRA' ? formData.projectId || undefined : undefined,
        observations: formData.observations || undefined,
        supplierCnpj: formData.supplierCnpj || undefined,
        supplierName: formData.supplierName || undefined,
        items: cleanedItems.length > 0 ? cleanedItems : undefined,
      })
    },
    onSuccess: () => {
      toast.success(editMode ? 'Alteracoes salvas!' : 'Nota fiscal confirmada!')
      setEditMode(false)
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const retryOcrMutation = useMutation({
    mutationFn: () => invoicesAPI.retryOcr(id!),
    onSuccess: () => {
      toast.success('Re-processando OCR...')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const linkMutation = useMutation({
    mutationFn: (transactionId: string) => invoicesAPI.linkTransaction(id!, transactionId),
    onSuccess: () => {
      toast.success('Transacao vinculada!')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const unlinkMutation = useMutation({
    mutationFn: () => invoicesAPI.unlinkTransaction(id!),
    onSuccess: () => {
      toast.success('Transacao desvinculada!')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const deleteMutation = useMutation({
    mutationFn: () => invoicesAPI.delete(id!),
    onSuccess: (data: any) => {
      toast.success(data.message || 'Nota fiscal excluida!')
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      navigate('/invoices')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  // ==================== LOADING ====================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>
        <p className="text-neutral-500">Nota fiscal nao encontrada.</p>
      </div>
    )
  }

  // ==================== CONFIDENCE BADGE ====================

  function renderConfidenceBadge(confidence: number | null) {
    if (confidence == null) return null
    const pct = Math.round(confidence)
    let color = 'bg-red-100 text-red-700'
    if (pct >= 70) color = 'bg-green-100 text-green-700'
    else if (pct >= 40) color = 'bg-yellow-100 text-yellow-700'
    return <Badge className={color}>OCR {pct}%</Badge>
  }

  // ==================== IMAGE SECTION ====================

  function renderImageSection() {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Imagem da Nota Fiscal</CardTitle>
        </CardHeader>
        <CardContent>
          {imageBlobUrl ? (
            <a href={imageBlobUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={imageBlobUrl}
                alt="Nota Fiscal"
                className="w-full rounded border cursor-pointer hover:opacity-90 transition-opacity"
              />
            </a>
          ) : (
            <div className="flex items-center justify-center h-48 border rounded bg-neutral-50">
              <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  // ==================== PROCESSING VIEW ====================

  function renderProcessing() {
    return (
      <div className="col-span-full flex flex-col items-center justify-center py-20 space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        <p className="text-lg text-neutral-600">Processando OCR...</p>
      </div>
    )
  }

  // ==================== EDIT FORM (REVIEW or CONFIRMED edit) ====================

  function renderEditForm() {
    const isUpdate = editMode // true when editing a CONFIRMED invoice

    const updateItem = (key: string, field: 'description' | 'quantity' | 'unit' | 'unitPrice' | 'totalPrice', value: string) => {
      setFormData((p) => ({
        ...p,
        items: p.items.map((it) => {
          if (it._key !== key) return it
          const updated = { ...it, [field]: value }
          if (field === 'quantity' || field === 'unitPrice') {
            const qty = Number(field === 'quantity' ? value : updated.quantity) || 0
            const price = Number(field === 'unitPrice' ? value : updated.unitPrice) || 0
            updated.totalPrice = qty && price ? (qty * price).toFixed(2) : ''
          }
          return updated
        }),
      }))
    }

    const addItem = () => {
      setFormData((p) => ({
        ...p,
        items: [
          ...p.items,
          {
            description: '',
            quantity: '',
            unit: '',
            unitPrice: '',
            totalPrice: '',
            _key: `_new_${Math.random().toString(36).slice(2)}`,
          },
        ],
      }))
    }

    const removeItem = (key: string) => {
      setFormData((p) => ({
        ...p,
        items: p.items.filter((it) => it._key !== key),
      }))
    }

    return (
      <div className="space-y-6">
        {/* Supplier (editable) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Fornecedor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="supplierName">Nome</Label>
              <Input
                id="supplierName"
                value={formData.supplierName}
                onChange={(e) => setFormData((p) => ({ ...p, supplierName: e.target.value }))}
                placeholder="Nome do fornecedor"
              />
            </div>
            <div>
              <Label htmlFor="supplierCnpj">CNPJ</Label>
              <Input
                id="supplierCnpj"
                value={formData.supplierCnpj}
                onChange={(e) => setFormData((p) => ({ ...p, supplierCnpj: e.target.value }))}
                placeholder="00.000.000/0000-00"
              />
            </div>
            {!isUpdate && (
              invoice.supplier ? (
                <Badge className="bg-green-100 text-green-700">Fornecedor encontrado</Badge>
              ) : invoice.ocrSupplierCnpj ? (
                <Badge className="bg-yellow-100 text-yellow-700">Novo fornecedor</Badge>
              ) : null
            )}
          </CardContent>
        </Card>

        {/* Editable Fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados da Nota</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="invoiceNumber">Numero da NF</Label>
              <Input
                id="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData((p) => ({ ...p, invoiceNumber: e.target.value }))}
                placeholder="Ex: 001234"
              />
            </div>

            <div>
              <Label htmlFor="issueDate">Data de Emissao</Label>
              <Input
                id="issueDate"
                type="date"
                value={formData.issueDate}
                onChange={(e) => setFormData((p) => ({ ...p, issueDate: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="totalValue">Valor Total</Label>
              <Input
                id="totalValue"
                type="number"
                step="0.01"
                value={formData.totalValue}
                onChange={(e) => setFormData((p) => ({ ...p, totalValue: e.target.value }))}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label>Tipo</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData((p) => ({ ...p, type: v as 'OBRA' | 'OPERACIONAL' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OBRA">Obra</SelectItem>
                  <SelectItem value="OPERACIONAL">Operacional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'OBRA' && (
              <div>
                <Label>Projeto</Label>
                <Select
                  value={formData.projectId}
                  onValueChange={(v) => setFormData((p) => ({ ...p, projectId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(projects) &&
                      projects.map((project: any) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="observations">Observacoes</Label>
              <textarea
                id="observations"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formData.observations}
                onChange={(e) => setFormData((p) => ({ ...p, observations: e.target.value }))}
                placeholder="Observacoes adicionais..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Items (editable) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Itens</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus className="mr-1 h-3 w-3" /> Adicionar
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {formData.items.length === 0 ? (
              <p className="text-sm text-neutral-500">
                Nenhum item. Clique em "Adicionar" para incluir.
              </p>
            ) : (
              formData.items.map((item) => (
                <div
                  key={item._key}
                  className="grid grid-cols-12 gap-2 items-end border-b pb-3 last:border-0 last:pb-0"
                >
                  <div className="col-span-12 sm:col-span-4">
                    <Label className="text-xs">Descricao</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(item._key, 'description', e.target.value)}
                      placeholder="Descricao"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-xs">Qtd</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={item.quantity}
                      onChange={(e) => updateItem(item._key, 'quantity', e.target.value)}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <Label className="text-xs">Unid</Label>
                    <Input
                      value={item.unit}
                      onChange={(e) => updateItem(item._key, 'unit', e.target.value)}
                      placeholder="UN"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-xs">Preco Unit.</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(item._key, 'unitPrice', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <Label className="text-xs">Total</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.totalPrice}
                      onChange={(e) => updateItem(item._key, 'totalPrice', e.target.value)}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end pb-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item._key)}
                      className="text-red-600 hover:bg-red-50 h-8 w-8"
                      title="Remover item"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* OCR Confidence (only in REVIEW) */}
        {!isUpdate && (
          <div className="flex items-center gap-2">
            {renderConfidenceBadge(invoice.ocrConfidence)}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending}
          >
            {confirmMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {isUpdate ? 'Salvar Alteracoes' : 'Confirmar Dados'}
          </Button>
          {isUpdate ? (
            <Button
              variant="outline"
              onClick={() => setEditMode(false)}
              disabled={confirmMutation.isPending}
            >
              Cancelar
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => retryOcrMutation.mutate()}
              disabled={retryOcrMutation.isPending}
            >
              {retryOcrMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Re-processar OCR
            </Button>
          )}
        </div>
      </div>
    )
  }

  // ==================== CONFIRMED VIEW ====================

  function renderConfirmed() {
    return (
      <div className="space-y-6">
        {/* Edit button */}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
            <Pencil className="mr-2 h-3 w-3" /> Editar
          </Button>
        </div>

        {/* Read-only Fields */}
        {renderReadOnlyFields()}

        {/* Transaction Suggestions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" /> Vinculacao Financeira
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSuggestions ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-neutral-500">Buscando transacoes...</span>
              </div>
            ) : suggestions && (suggestions as any[]).length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-neutral-500">
                  Transacoes compativeis (mesmo fornecedor ou mesmo valor):
                </p>
                {(suggestions as any[]).map((tx: any) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between rounded-lg border p-3 gap-3"
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{tx.description || tx.rawDescription}</p>
                      {tx.matchReasons && tx.matchReasons.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {tx.matchReasons.includes('supplier') && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0 font-normal">
                              Mesmo fornecedor
                            </Badge>
                          )}
                          {tx.matchReasons.includes('amount') && (
                            <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 font-normal">
                              Mesmo valor
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className="flex gap-3 text-xs text-neutral-500">
                        <span>{formatDate(tx.date)}</span>
                        <span>{formatCurrency(tx.amount)}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => linkMutation.mutate(tx.id)}
                      disabled={linkMutation.isPending}
                    >
                      {linkMutation.isPending ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <Link2 className="mr-1 h-3 w-3" />
                      )}
                      Vincular
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 py-4">
                Nenhuma transacao compativel encontrada.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // ==================== LINKED VIEW ====================

  function renderLinked() {
    return (
      <div className="space-y-6">
        {renderReadOnlyFields()}

        {/* Linked Transaction */}
        {invoice.transaction && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="h-4 w-4" /> Transacao Vinculada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-neutral-400" />
                <span>{formatDate(invoice.transaction.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="h-4 w-4 text-neutral-400" />
                <span>{formatCurrency(invoice.transaction.amount)}</span>
              </div>
              <p className="text-sm">{invoice.transaction.description}</p>
              {invoice.transaction.category && (
                <Badge variant="outline">{invoice.transaction.category}</Badge>
              )}
              <Separator />
              <Button
                variant="outline"
                size="sm"
                onClick={() => unlinkMutation.mutate()}
                disabled={unlinkMutation.isPending}
              >
                {unlinkMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Unlink className="mr-2 h-4 w-4" />
                )}
                Desvincular
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  // ==================== READ-ONLY FIELDS ====================

  function renderReadOnlyFields() {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados da Nota</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-500 flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Numero
                </span>
                <p className="font-medium">{invoice.invoiceNumber || '-'}</p>
              </div>
              <div>
                <span className="text-neutral-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Data de Emissao
                </span>
                <p className="font-medium">{formatDate(invoice.issueDate)}</p>
              </div>
              <div>
                <span className="text-neutral-500 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Valor Total
                </span>
                <p className="font-medium">{formatCurrency(invoice.totalValue)}</p>
              </div>
              <div>
                <span className="text-neutral-500">Tipo</span>
                <p className="font-medium">{TYPE_LABELS[invoice.type] || invoice.type}</p>
              </div>
            </div>
            {invoice.project && (
              <div className="text-sm">
                <span className="text-neutral-500">Projeto</span>
                <p className="font-medium">{invoice.project.name}</p>
              </div>
            )}
            {invoice.observations && (
              <div className="text-sm">
                <span className="text-neutral-500">Observacoes</span>
                <p>{invoice.observations}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supplier */}
        {(invoice.supplier || invoice.ocrSupplierName) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Fornecedor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <p className="font-medium">
                {invoice.supplier?.name || invoice.ocrSupplierName}
              </p>
              {(invoice.supplier?.document || invoice.ocrSupplierCnpj) && (
                <p className="text-neutral-500">
                  CNPJ: {formatDocument(invoice.supplier?.document || invoice.ocrSupplierCnpj)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Items */}
        {invoice.items && invoice.items.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Itens</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium">Descricao</th>
                      <th className="pb-2 font-medium text-right">Qtd</th>
                      <th className="pb-2 font-medium">Unid</th>
                      <th className="pb-2 font-medium text-right">Preco Unit.</th>
                      <th className="pb-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item: any, idx: number) => (
                      <tr key={item.id || idx} className="border-b last:border-0">
                        <td className="py-2">{item.description}</td>
                        <td className="py-2 text-right">{item.quantity}</td>
                        <td className="py-2">{item.unit || '-'}</td>
                        <td className="py-2 text-right">{formatCurrency(item.unitPrice)}</td>
                        <td className="py-2 text-right">{formatCurrency(item.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {renderConfidenceBadge(invoice.ocrConfidence) && (
          <div className="flex items-center gap-2">
            {renderConfidenceBadge(invoice.ocrConfidence)}
          </div>
        )}
      </>
    )
  }

  // ==================== RIGHT COLUMN CONTENT BY STATUS ====================

  function renderRightColumn() {
    if (invoice.status === 'PROCESSING') return renderProcessing()
    if (invoice.status === 'REVIEW') return renderEditForm()
    if (invoice.status === 'CONFIRMED') return editMode ? renderEditForm() : renderConfirmed()
    if (invoice.status === 'LINKED') return renderLinked()
    return renderReadOnlyFields()
  }

  // ==================== RENDER ====================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold">
                NF {invoice.invoiceNumber || '#' + invoice.id.slice(0, 8)}
              </h1>
              <Badge className={STATUS_COLORS[invoice.status] || ''}>
                {STATUS_LABELS[invoice.status] || invoice.status}
              </Badge>
              <Badge variant="outline">{TYPE_LABELS[invoice.type] || invoice.type}</Badge>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Criada em {formatDate(invoice.createdAt)}
            </p>
          </div>
        </div>

        {/* Delete button */}
        {canDelete && invoice.status !== 'LINKED' && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Excluir
          </Button>
        )}
      </div>

      <Separator />

      {/* Two-column layout (image left, data right) */}
      {invoice.status === 'PROCESSING' ? (
        renderProcessing()
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Image */}
          <div>{renderImageSection()}</div>

          {/* Right: Data sections by status */}
          <div>{renderRightColumn()}</div>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Excluir Nota Fiscal
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta nota fiscal? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
